<template>
    <div
        :class="classTextArea"
        class="text-area"
    >
        <label
            v-if="label"
            :for="id"
            class="label"
        >
            {{ labelWithRequiredIndicator }}
        </label>

        <textarea
            :id="id"
            ref="textareaElement"
            v-model="value"
            :name="id"
            :placeholder="placeholderWithRequiredIndicator"
            :readonly="readonly"
            class="textarea"
            type="text"
            @keyup.enter.prevent.stop=""
        ></textarea>

        <FieldError
            v-if="error"
            :error="error"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import FieldError from '@/components/ui/inputs/ThFieldError.vue'
import { useFocus } from '@vueuse/core'

interface Props {
    label?: string
    placeholder?: string
    required?: boolean
    error?: string
    readonly?: boolean
    id: string
    focus?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    label: undefined,
    placeholder: undefined,
    required: undefined,
    error: undefined,
    readonly: undefined,
    focus: false,
})

const value = defineModel<string | null>('modelValue')

const labelWithRequiredIndicator = computed(() =>
    props.label && props.required ? `${props.label}*` : props.label
)

const placeholderWithRequiredIndicator = computed(() =>
    !props.label && props.required && props.placeholder
        ? `${props.placeholder}*`
        : props.placeholder
)

const classTextArea = computed(() => ({
    error: Boolean(props.error),
}))

const textareaElement = ref<HTMLTextAreaElement | null>(null)

useFocus(textareaElement, { initialValue: props.focus })

watch(
    [value, textareaElement],
    () => {
        if (textareaElement.value) {
            textareaElement.value.style.height = 'auto'
            textareaElement.value.style.height = `${textareaElement.value.scrollHeight}px`
        }
    },
    {
        immediate: true,
    }
)
</script>
<style lang="scss" scoped>
.text-area {
    width: 100%;
    font-family: $effra-font-stack;
    font-weight: 400;
    line-height: 1.25rem;

    .label {
        display: block;
        font-size: 0.75rem;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        color: $slate;
        margin-bottom: 5px;
    }

    .textarea {
        display: block;
        width: 100%;
        height: 50px;
        padding: 15px 10px;
        border: 1px solid rgba($slate, 0.5);
        border-radius: 2px;
        font-size: 1rem;
        line-height: 1.25rem;
        min-height: 96px;
        color: $slate;

        &:focus {
            background: $white;
            border-color: $slate;
        }

        &::placeholder {
            color: $grey-400;
        }

        &:read-only {
            color: $grey-500;
        }
    }

    &.error {
        .textarea {
            border-color: rgba($red, 0.7);
        }
    }
}
</style>
